<template>
    <!-- START: form -->
    <div class="form">
        <!-- START: formular -->
        <form class="needs-validation" novalidate @submit.prevent="onSubmit">

            <h2 id="lieferadresse">Ihre Lieferadresse</h2>

            <div class="row">
                <div class="col-sm-6">
                    <select-input name="lieferadresse_anrede" label="Anrede" validation="required" custom-error-message="Bitte wählen Sie eine Anrede aus." autocomplete="honorific-prefix">
                        <option selected="selected" value="">Bitte wählen</option>
                        <option v-for="(value, index) in anredeOptionen" :key="index" :value="value">{{ value }}</option>
                    </select-input>
                </div>
                <div class="col-sm-6">
                    <text-input name="lieferadresse_titel" label="Titel"/>
                </div>
            </div>

            <div class="row" v-if="formData.lieferadresse_anrede == 'Firma'">
                <div class="col-sm">
                    <text-input name="lieferadresse_firma" label="Firmenname" validation="required" custom-error-message="Bitte geben Sie einen Firmennamen an." autocomplete="organization"/>
                </div>
            </div>

            <div class="row">
                <div class="col-sm-6">
                    <text-input name="lieferadresse_vorname" label="Vorname" validation="required" custom-error-message="Bitte geben Sie einen Vornamen an." autocomplete="given-name"/>
                </div>
                <div class="col-sm-6">
                    <text-input name="lieferadresse_nachname" label="Name" validation="required" custom-error-message="Bitte geben Sie einen Nachnamen an." autocomplete="family-name"/>
                </div>
            </div>

            <div class="row" v-if="['Eheleute', 'Lebensgemeinschaft', 'Erbengemeinschaft', 'Wohngemeinschaft', 'Eigentümergemeinschaft'].indexOf((formData.lieferadresse_anrede || '').trim()) !== -1">
                <div class="col-sm-6">
                    <text-input
                        name="lieferadresse_vorname2"
                        label="Vorname zweite Person"
                        validation="required"
                        custom-error-message="Bitte geben Sie einen Vornamen für die zweite Person an"
                    />
                </div>
                <div class="col-sm-6">
                    <text-input
                        custom-error-message="Bitte geben Sie einen Nachnamen für die zweite Person an."
                        label="Nachname zweite Person"
                        name="lieferadresse_nachname2"
                        validation="required"
                    />
                </div>
            </div>

            <div class="row">
                <div class="col-sm-6">
                    <datepicker-input
                        name="lieferadresse_geburtsdatum"
                        label="Geburtsdatum"
                        validation="required, date, age:18"
                        :custom-error-message="{required: 'Bitte geben Sie ein Geburtsdatum ein.'}"
                        :options="{maxDate: subtractFromDate(new Date(), 18), view: 'years'}"
                        autocomplete="bday"
                    />
                </div>
            </div>

            <div class="row">
                <div class="col-sm-6">
                    <combobox-input
                        name="lieferadresse_plz"
                        label="PLZ"
                        validation="required"
                        custom-error-message="Bitte geben Sie eine PLZ ein."
                        :options="plzLieferadresseOptions"
                        @changed="onChangeLieferadressePLZ"
                        @select-item="form.setValue('lieferadresse_ort', $event.ort)"
                        :loading="plzLieferadresseLoading"
                        readonly="readonly"
                        autocomplete="postal-code"
                    >
                        <template #prependOption v-if="!plzLieferadresseLoading && formData.lieferadresse_plz && formData.lieferadresse_plz.length >= 3 && formData.lieferadresse_plz.length < 5 && plzLieferadresseOptions.length === 0">
                            PLZ konnte nicht gefunden werden.
                        </template>
                    </combobox-input>
                </div>
                <div class="col-sm-6">
                    <text-input
                        name="lieferadresse_ort"
                        label="Ort"
                        validation="required"
                        custom-error-message="Bitte geben Sie einen Ort an."
                        readonly="readonly"
                        autocomplete="address-level2"
                    />
                </div>
            </div>

            <div class="row">
                <div class="col-sm-6">
                    <combobox-input
                        name="lieferadresse_strasse"
                        label="Straße"
                        validation="required"
                        custom-error-message="Bitte geben Sie eine Straße an."
                        :options="strasseLieferadresseOptions"
                        @changed="onChangeLieferadresseStrasse"
                        :loading="strasseLieferadresseLoading"
                        autocomplete="street-address"
                    >
                        <template #prependOption v-if="!strasseLieferadresseLoading && formData.lieferadresse_strasse && strasseLieferadresseOptions.length === 0">
                            Straße konnte nicht gefunden werden.
                        </template>
                    </combobox-input>
                </div>
                <div class="col-sm-3">
                    <text-input
                        name="lieferadresse_hausnummer"
                        label="Hausnr."
                        validation="required"
                        custom-error-message="Bitte geben Sie eine Hausnummer an."
                    />
                </div>
                <div class="col-sm-3">
                    <text-input
                        name="lieferadresse_hausnummer_zusatz"
                        label="Zusatz"
                        autocomplete="address-line3"
                    />
                </div>
            </div>

            <div class="form-collapse">
                <checkbox-input name="rechnungsadresse_abweichend" label="Abweichende Rechnungsadresse" />
                <div id="abweichAdr" v-show="formData.rechnungsadresse_abweichend" >
                    <div class="row">
                        <div class="col-sm-6">
                            <combobox-input
                                name="rechnungsadresse_plz" label="PLZ"
                                validation="required:if:rechnungsadresse_abweichend"
                                custom-error-message="Bitte geben Sie eine PLZ für die abweichende Rechnungsadresse an."
                                @changed="onChangeRechnungsadressePLZ"
                                :options="plzRechnungsadresseOptions"
                                @select-item="form.setValue('rechnungsadresse_ort', $event.ort)"
                                :loading="plzRechnungsadresseLoading"
                            >
                                <template #prependOption v-if="!plzRechnungsadresseLoading && formData.rechnungsadresse_plz && formData.rechnungsadresse_plz.length >= 3 && formData.rechnungsadresse_plz.length < 5 && plzRechnungsadresseOptions.length === 0">
                                    PLZ konnte nicht gefunden werden.
                                </template>
                            </combobox-input>
                        </div>
                        <div class="col-sm-6">
                            <text-input
                                name="rechnungsadresse_ort"
                                label="Ort"
                                validation="required:if:rechnungsadresse_abweichend"
                                custom-error-message="Bitte geben Sie einen Ort für die abweichende Rechnungsadresse an."
                            />
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-sm-6">
                            <combobox-input
                                name="rechnungsadresse_strasse"
                                label="Straße"
                                validation="required:if:rechnungsadresse_abweichend"
                                custom-error-message="Bitte geben Sie eine Straße für die abweichende Rechnungsadresse an."
                                :options="strasseRechnungsadresseOptions"
                                @changed="onChangeRechnungsadresseStrasse"
                                :loading="strasseRechnungsadresseLoading"
                            >
                                <template #prependOption v-if="!strasseRechnungsadresseLoading && formData.rechnungsadresse_strasse && strasseRechnungsadresseOptions.length === 0">
                                    Straße konnte nicht gefunden werden.
                                </template>
                            </combobox-input>
                        </div>
                        <div class="col-sm-3">
                            <text-input
                                name="rechnungsadresse_hausnummer"
                                label="Hausnr."
                                validation="required:if:rechnungsadresse_abweichend"
                                custom-error-message="Bitte geben Sie eine Hausnummer für die abweichende Rechnungsadresse an."
                            />
                        </div>
                        <div class="col-sm-3">
                            <text-input name="rechnungsadresse_hausnummer_zusatz" label="Zusatz"/>
                        </div>
                    </div>
                </div>
            </div>


            <h2 id="contact">Ihre Kontaktdaten</h2>

            <div class="row">
                <div class="col-sm-6">
                    <text-input
                        name="email"
                        type="email"
                        label="E-Mail-Adresse"
                        validation="required,email"
                        :custom-error-message="{
                            required: 'Bitte geben Sie eine E-Mail an.',
                            email: 'Sie haben keine valide E-Mail angegeben. Bitte kontrollieren Sie die E-Mail.'
                        }"
                        autocomplete="email"
                    />
                </div>
<!--                <div class="col-sm-6">-->
<!--                    <text-input name="email_wdh" type="email" label="E-Mail-Adresse bestätigen"-->
<!--                                validation="required,email,sameAs:email"-->
<!--                                custom-error-message="Ihre E-Mailadresse stimmt nicht überein."/>-->
<!--                </div>-->
            </div>
            <div class="row">
                <div class="col-sm-6">
                    <select-input
                        name="telefon_typ"
                        label="Telefon"
                        validation="required"
                        custom-error-message="Bitte wählen Sie einen Telefontypen aus."
                    >
                        <option selected="selected" value="">Bitte wählen</option>
                        <option v-for="(value, index) in telefonTyp" :key="index" :value="value">{{ value }}</option>
                    </select-input>
                </div>
                <div class="col-sm-6">
                    <text-input
                        name="telefon"
                        label="Telefonnummer"
                        validation="required"
                        custom-error-message="Bitte geben Sie eine Telefonnummer an."
                        autocomplete="tel"
                    />
                </div>
            </div>

            <p><small>Mit * markierte Felder sind Pflichtfelder.</small></p>

            <div class="form-buttons">
                <button class="btn btn-secondary btn-back" type="button" @click.prevent="$emit('prev')">Zurück</button>
                <button class="btn btn-secondary btn-next" type="button" @click.prevent="$emit('next')">Weiter</button>
            </div>


        </form>
        <!-- END: formular -->
    </div>
    <!-- END: form -->
</template>
<script>
import DatepickerInput from "@/components/Form/DatepickerInput";
import TextInput from "@/components/Form/TextInput";
import SelectInput from "@/components/Form/SelectInput"
import CheckboxInput from "@/components/Form/CheckboxInput"
import ComboboxInput from "@/components/Form/ComboboxInput";
import {getOrtFromPlz} from "@/service/getPLZ";
import {getStrasse} from "@/service/getStrasse";
import FormAware from "@/mixins/FormAware";

export default {
    name: 'BestellstreckeFormAdressen',
    components: {SelectInput, DatepickerInput, TextInput, CheckboxInput, ComboboxInput},
    mixins: [FormAware],
    props: ['formData'],
    inject: ['settings'],
    computed: {
        anredeOptionen: function () {
            return this.settings.anredeOptionen.split('\n');
        },
    },
    mounted() {
        if (typeof window.tab1mounted === 'function') {
            window.tab1mounted();
        }
    },
    data: () => ({
       plzLieferadresseOptions: [],
       plzLieferadresseLoading: false,

       strasseLieferadresseOptions: [],
       strasseLieferadresseLoading: false,

       plzRechnungsadresseOptions: [],
       plzRechnungsadresseLoading: false,

        strasseRechnungsadresseOptions: [],
        strasseRechnungsadresseLoading: false,

        telefonTyp: {
           'Festnetz': 'Festnetz',
            'Mobil': 'Mobil'
        }
    }),
    methods: {
        onSubmit() {
            // console.log('submit tab 1')
        },
        onChangeLieferadresseStrasse() {
            const [plz, ort, strasse] = [
                this.formData.lieferadresse_plz,
                this.formData.lieferadresse_ort,
                this.formData.lieferadresse_strasse,
            ];
            if (plz && ort && strasse) {
                this.strasseLieferadresseLoading = true;
                getStrasse(this.settings.strasseUrl, plz, ort, strasse)
                    .then(data => {
                        this.strasseLieferadresseLoading = false;
                        if (data) {
                            this.strasseLieferadresseOptions = data.map(strasse => ({
                                label: strasse,
                                value: strasse,
                            }));
                        }
                    })
                    .catch(() => {
                        this.strasseLieferadresseLoading = false;
                    }); // do nothing on error
            } else {
                this.strasseLieferadresseOptions = [];
            }
        },
        onChangeRechnungsadresseStrasse() {
            const [plz, ort, strasse] = [
                this.formData.rechnungsadresse_plz,
                this.formData.rechnungsadresse_ort,
                this.formData.rechnungsadresse_strasse,
            ];
            if (plz && ort && strasse) {
                this.strasseRechnungsadresseLoading = true;
                getStrasse(this.settings.strasseUrl, plz, ort, strasse)
                    .then(data => {
                        this.strasseRechnungsadresseLoading = false;
                        if (data) {
                            this.strasseRechnungsadresseOptions = data.map(strasse => ({
                                label: strasse,
                                value: strasse,
                            }));
                        }
                    })
                    .catch(() => {
                        this.strasseRechnungsadresseLoading = false;
                    }); // do nothing on error
            } else {
                this.strasseRechnungsadresseOptions = [];
            }
        },
        onChangeLieferadressePLZ(plz) {
            if (plz && plz.length >= 3) {
                this.plzLieferadresseLoading = true;
                getOrtFromPlz(this.settings.plzUrl, plz)
                    .then(data => {
                        this.plzLieferadresseLoading = false;
                        if (data) {
                            this.plzLieferadresseOptions = data.map(plzData => ({
                                label: plzData.plz + ' ' + plzData.ort,
                                value: plzData.plz,
                                ort: plzData.ort,
                            }));
                        }
                    })
                    .catch(() => {
                        this.plzLieferadresseLoading = false;
                    }); // do nothing on error
            } else {
                this.plzLieferadresseOptions = [];
            }
        },
        onChangeRechnungsadressePLZ(plz) {
            if (plz && plz.length >= 3) {
                this.plzRechnungsadresseLoading = true;
                getOrtFromPlz(this.settings.plzUrl, plz)
                    .then(data => {
                        this.plzRechnungsadresseLoading = false;
                        if (data) {
                            this.plzRechnungsadresseOptions = data.map(plzData => ({
                                label: plzData.plz + ' ' + plzData.ort,
                                value: plzData.plz,
                                ort: plzData.ort,
                            }));
                        }
                    })
                    .catch(() => {
                        this.plzRechnungsadresseLoading = false;
                    }); // do nothing on error
            } else {
                this.plzRechnungsadresseOptions = [];
            }
        },
        subtractFromDate(date, years, months, days) {
            const subtractedDate = new Date(date.getTime());
            if (typeof years === 'number')
                subtractedDate.setFullYear(date.getFullYear() - years);
            if (typeof months === 'number')
                subtractedDate.setMonth(date.getMonth() - months);
            if (typeof days === 'number')
                subtractedDate.setDate(date.getDate() - days);
            return subtractedDate;
        },
    },
}
</script>
